import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import HoverObserver from "./hover-observer";
import HoverAnimator from "./hover-animator";
import LinkObject from "./link-object";

export default class SiteMap {

    constructor(element) {
        this._frame = this._frame.bind(this);

        this._element = element;

        this._scene = new THREE.Scene();

        this._camera = new THREE.PerspectiveCamera();
        this._repositionCamera();

        this._renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        this._renderer.shadowMap.enabled = true;
        this._renderer.shadowMap.type = THREE.PCFShadowMap;
        this._renderer.outputEncoding = THREE.sRGBEncoding;

        this._controls = new OrbitControls(this._camera, this._renderer.domElement);
        this._controls.minDistance = 4;
        this._controls.maxDistance = 10;
        this._controls.maxAzimuthAngle = 1.5 * Math.PI;
        this._controls.minAzimuthAngle = 0.5 * Math.PI;
        this._controls.maxPolarAngle = 0.5 * Math.PI;

        this._running = false;
        this._paused = false;
    }

    async loadScene(url) {
        const loader = new THREE.ObjectLoader();
        const scene = await loader.loadAsync(url);
        this._scene.add(scene);

        this._controls.target = scene.position;
    }

    async start() {
        this._updateSize();
        this._observeResize();
        this._element.appendChild(this._renderer.domElement);

        this._running = true;
        requestAnimationFrame(this._frame);
    }

    setHoverAnimations(config) {
        this._animationHoverObserver?.unobserve();

        this._animationHoverObserver = new HoverObserver();
        this._animationHoverObserver.observe(this._element);

        this._hoverAnimator = new HoverAnimator(this._scene);

        for (const [objectName, animationName] of Object.entries(config)) {
            const obj = this._scene.getObjectByName(objectName);
            if (!obj || !animationName) {
                continue;
            }

            const animation = this._getAnimationByName(animationName);

            if (animation) {
                this._hoverAnimator.addObject(obj, animation);
            }
        }

        this._hoverAnimator.observe(this._animationHoverObserver);
    }

    setHoverLinks(config) {
        this._linkHoverObserver?.unobserve();

        this._linkHoverObserver = new HoverObserver();
        this._linkHoverObserver.observe(this._element);

        for (const [name, href] of Object.entries(config)) {
            const object = this._scene.getObjectByName(name);
            if (!object || !href) {
                continue;
            }

            const linkObject = new LinkObject(object, href);
            linkObject.observe(this._linkHoverObserver);
        }
    }

    get dimensions() {
        const { width, height } = this._element.getBoundingClientRect();
        return { width, height };
    }

    set paused(paused) {
        if (paused === true) {
            this._paused = true;
        } else {
            this._paused = false;
            if (!this._running) {
                this._running = true;
                requestAnimationFrame(this._frame);
            }
        }
    }

    _updateSize() {
        const { width, height } = this.dimensions;

        this._camera.aspect = width / height;
        this._camera.updateProjectionMatrix();

        this._renderer.setSize(width, height);
    }

    _observeResize() {
        const ro = new ResizeObserver(entries => {
            entries.forEach(() => this._updateSize());
        });

        ro.observe(this._element);
        this._resizeObserver = ro;
    }

    _repositionCamera() {
        const angle = 1.25 * Math.PI;
        const distance = 8.0;

        const sinAngle = Math.sin(angle);
        const cosAngle = Math.cos(angle);

        const x = distance * cosAngle * sinAngle;
        const y = distance * sinAngle * sinAngle;
        const z = distance * cosAngle;

        this._camera.position.set(x, y, z);
    }

    _frame() {
        if (this._running) {
            if (this._paused) {
                this._running = false;
                return;
            }
            requestAnimationFrame(this._frame);
        }

        this._controls.update();

        this._linkHoverObserver?.update(this._camera);
        this._animationHoverObserver?.update(this._camera);

        this._hoverAnimator?.update();

        this._renderer.render(this._scene, this._camera);
    }

    _getAnimationByName(name) {
        function find(node) {
            const a = node.animations.find(a => a.name === name);
            if (a) {
                return a;
            }

            for (const child of node.children) {
                const a = find(child);
                if (a) {
                    return a;
                }
            }
        }

        return find(this._scene);
    }

}
